<template>
  <div class="public-view min-vh-100 d-flex align-items-center py-4">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-12 col-md-7 col-lg-6">
          <!-- LOADING -->
          <div v-if="loading" class="glass-card text-center py-5 shadow">
            <div class="spinner-border text-success" role="status"></div>
            <p class="mt-3 fw-bold text-cafe">Consultando información...</p>
          </div>

          <!-- NO ENCONTRADO -->
          <div v-else-if="!maquina" class="glass-card p-5 text-center">
            <i
              class="bi bi-exclamation-triangle text-warning mb-3"
              style="font-size: 3.5rem"
            ></i>
            <h4 class="fw-black text-cafe">Máquina no encontrada</h4>
            <p class="text-muted">
              El identificador no corresponde a ninguna máquina activa.
            </p>
          </div>

          <!-- DATOS DE LA MÁQUINA -->
          <div v-else class="card shadow-2xl border-0 rounded-4 overflow-hidden">
            <!-- ── HEADER ─────────────────────────────────────────── -->
            <div
              class="header-maquina d-flex align-items-center justify-content-center px-3 py-3"
            >
              <img
                src="@/assets/img/Divisan/logo_divisan.png"
                class="brand-logo-img"
                alt="Divisan"
              />
            </div>

            <!-- ── CRITICIDAD INLINE ──────────────────────────────── -->
            <div class="criticidad-wrapper">
              <div class="criticidad-inline">
                <span :class="['criticidad-dot-inline', clsCriticidad]"></span>
                <span class="criticidad-tag-inline">Criticidad</span>
                <span :class="['criticidad-valor-inline', clsCriticidad + '-txt']">
                  {{ maquina.criticidad }}
                </span>
                <span class="criticidad-sep">·</span>
                <span class="criticidad-desc-inline">{{ descCriticidad }}</span>
              </div>
            </div>

            <!-- ── BODY ───────────────────────────────────────────── -->
            <div class="card-body p-3 p-md-4">
              <!-- INFO BÁSICA -->
              <div class="info-grid mb-4">
                <div class="info-item info-item-nombre">
                  <span class="label-mini">Máquina</span>
                  <span class="value-mini value-nombre">{{ maquina.nombre }}</span>
                </div>
                <div class="info-item">
                  <span class="label-mini">Marca</span>
                  <span class="value-mini">{{ maquina.marca }}</span>
                </div>
                <div class="info-item">
                  <span class="label-mini">Serie / ID</span>
                  <span class="value-mini">{{ maquina.serie }}</span>
                </div>
                <div class="info-item">
                  <span class="label-mini">Área</span>
                  <span class="value-mini">{{ maquina.area }}</span>
                </div>
              </div>

              <!-- GALERÍA DE LA MÁQUINA -->
              <div v-if="maquina.imagenes?.length" class="mb-4">
                <label class="label-custom">Imágenes de la máquina</label>
                <div id="carouselMaquina" class="carousel slide carousel-fade">
                  <div class="carousel-inner rounded-3">
                    <div
                      v-for="(img, idx) in maquina.imagenes"
                      :key="img.id"
                      :class="['carousel-item', { active: idx === 0 }]"
                    >
                      <img
                        :src="img.imagen"
                        class="d-block w-100 object-fit-cover"
                        style="height: 220px"
                        :alt="`Imagen ${idx + 1} de ${maquina.nombre}`"
                      />
                    </div>
                  </div>
                  <template v-if="maquina.imagenes.length > 1">
                    <button
                      class="carousel-control-prev"
                      type="button"
                      data-bs-target="#carouselMaquina"
                      data-bs-slide="prev"
                    >
                      <span class="carousel-control-prev-icon"></span>
                    </button>
                    <button
                      class="carousel-control-next"
                      type="button"
                      data-bs-target="#carouselMaquina"
                      data-bs-slide="next"
                    >
                      <span class="carousel-control-next-icon"></span>
                    </button>
                  </template>
                </div>
              </div>

              <!-- ── ÚLTIMO PREVENTIVO ──────────────────────────── -->
              <div class="mtto-seccion mb-3">
                <button
                  class="mtto-toggle"
                  :class="{ abierto: mostrarPreventivo }"
                  @click="mostrarPreventivo = !mostrarPreventivo"
                >
                  <div class="mtto-toggle-left">
                    <span class="mtto-toggle-dot preventivo-dot"></span>
                    <div>
                      <span class="mtto-toggle-label"
                        >Último mantenimiento preventivo</span
                      >
                      <span v-if="maquina.ultimo_preventivo" class="mtto-toggle-fecha">
                        {{ formatFechaCorta(maquina.ultimo_preventivo.fecha_inicio) }}
                      </span>
                      <span v-else class="mtto-toggle-ninguno">Sin registros</span>
                    </div>
                  </div>
                  <i
                    :class="[
                      'bi',
                      mostrarPreventivo ? 'bi-chevron-up' : 'bi-chevron-down',
                      'mtto-chevron',
                    ]"
                  ></i>
                </button>

                <Transition name="slide">
                  <div v-if="mostrarPreventivo" class="mtto-detalle">
                    <!-- Sin datos -->
                    <div v-if="!maquina.ultimo_preventivo" class="mtto-vacio">
                      <i class="bi bi-calendar-x me-2"></i>
                      No hay mantenimientos preventivos registrados.
                    </div>

                    <!-- Con datos -->
                    <template v-else>
                      <!-- Nombre de rutina: bloque completo, permite wrap -->
                      <div class="rutina-bloque mb-3">
                        <span class="label-mini">Rutina</span>
                        <span class="rutina-nombre">
                          {{ maquina.ultimo_preventivo.rutina_nombre }}
                        </span>
                      </div>

                      <div class="evento-card mb-3">
                        <div class="evento-item">
                          <span class="label-mini">Tipo</span>
                          <span class="value-mini">{{
                            maquina.ultimo_preventivo.rutina_tipo
                          }}</span>
                        </div>
                        <div class="evento-item">
                          <span class="label-mini">Fecha inicio</span>
                          <span class="value-mini">
                            {{ formatFechaCorta(maquina.ultimo_preventivo.fecha_inicio) }}
                          </span>
                        </div>
                        <div
                          class="evento-item"
                          v-if="maquina.ultimo_preventivo.fecha_fin"
                        >
                          <span class="label-mini">Fecha fin</span>
                          <span class="value-mini">
                            {{ formatFechaCorta(maquina.ultimo_preventivo.fecha_fin) }}
                          </span>
                        </div>
                        <div
                          class="evento-item"
                          v-if="maquina.ultimo_preventivo.duracion_horas"
                        >
                          <span class="label-mini">Duración real</span>
                          <span class="value-mini fw-bold text-cafe">
                            {{ maquina.ultimo_preventivo.duracion_horas }} h
                          </span>
                        </div>
                      </div>

                      <div v-if="maquina.ultimo_preventivo.notas" class="mb-3">
                        <label class="label-custom">Notas</label>
                        <div class="descripcion-box">
                          {{ maquina.ultimo_preventivo.notas }}
                        </div>
                      </div>

                      <!-- Carrusel evidencias preventivo -->
                      <div v-if="maquina.ultimo_preventivo.imagenes?.length">
                        <label class="label-custom">Evidencias</label>
                        <div id="carouselPreventivo" class="carousel slide">
                          <div class="carousel-inner rounded-3">
                            <div
                              v-for="(img, idx) in maquina.ultimo_preventivo.imagenes"
                              :key="img.id"
                              :class="['carousel-item', { active: idx === 0 }]"
                            >
                              <img
                                :src="img.imagen"
                                class="d-block w-100 object-fit-cover"
                                style="height: 240px"
                                alt="Evidencia preventivo"
                              />
                            </div>
                          </div>
                          <template v-if="maquina.ultimo_preventivo.imagenes.length > 1">
                            <button
                              class="carousel-control-prev"
                              type="button"
                              data-bs-target="#carouselPreventivo"
                              data-bs-slide="prev"
                            >
                              <span class="carousel-control-prev-icon"></span>
                            </button>
                            <button
                              class="carousel-control-next"
                              type="button"
                              data-bs-target="#carouselPreventivo"
                              data-bs-slide="next"
                            >
                              <span class="carousel-control-next-icon"></span>
                            </button>
                          </template>
                        </div>
                      </div>
                    </template>
                  </div>
                </Transition>
              </div>

              <!-- ── ÚLTIMO CORRECTIVO ───────────────────────────── -->
              <div class="mtto-seccion">
                <button
                  class="mtto-toggle"
                  :class="{ abierto: mostrarCorrectivo }"
                  @click="mostrarCorrectivo = !mostrarCorrectivo"
                >
                  <div class="mtto-toggle-left">
                    <span class="mtto-toggle-dot correctivo-dot"></span>
                    <div>
                      <span class="mtto-toggle-label"
                        >Último mantenimiento correctivo</span
                      >
                      <span v-if="maquina.ultimo_correctivo" class="mtto-toggle-fecha">
                        {{ formatFechaSimple(maquina.ultimo_correctivo.fecha) }}
                      </span>
                      <span v-else class="mtto-toggle-ninguno">Sin registros</span>
                    </div>
                  </div>
                  <i
                    :class="[
                      'bi',
                      mostrarCorrectivo ? 'bi-chevron-up' : 'bi-chevron-down',
                      'mtto-chevron',
                    ]"
                  ></i>
                </button>

                <Transition name="slide">
                  <div v-if="mostrarCorrectivo" class="mtto-detalle">
                    <!-- Sin datos -->
                    <div v-if="!maquina.ultimo_correctivo" class="mtto-vacio">
                      <i class="bi bi-tools me-2"></i>
                      No hay mantenimientos correctivos registrados.
                    </div>

                    <!-- Con datos -->
                    <template v-else>
                      <div class="evento-card mb-3">
                        <div class="evento-item">
                          <span class="label-mini">Fecha</span>
                          <span class="value-mini">
                            {{ formatFechaSimple(maquina.ultimo_correctivo.fecha) }}
                          </span>
                        </div>
                        <div class="evento-item">
                          <span class="label-mini">Técnico</span>
                          <span class="value-mini">{{
                            maquina.ultimo_correctivo.tecnico
                          }}</span>
                        </div>
                        <div class="evento-item">
                          <span class="label-mini">Operador</span>
                          <span class="value-mini">{{
                            maquina.ultimo_correctivo.operador
                          }}</span>
                        </div>
                        <div class="evento-item">
                          <span class="label-mini">Tiempo de intervención</span>
                          <span class="value-mini fw-bold text-cafe">
                            {{ maquina.ultimo_correctivo.tiempo_display }}
                          </span>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="label-custom">Descripción de la falla</label>
                        <div class="descripcion-box falla-box">
                          {{ maquina.ultimo_correctivo.falla }}
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="label-custom">Reparación realizada</label>
                        <div class="descripcion-box">
                          {{ maquina.ultimo_correctivo.reparacion }}
                        </div>
                      </div>

                      <!-- Carrusel evidencias correctivo -->
                      <div v-if="maquina.ultimo_correctivo.imagenes?.length">
                        <label class="label-custom">Evidencias</label>
                        <div id="carouselCorrectivo" class="carousel slide">
                          <div class="carousel-inner rounded-3">
                            <div
                              v-for="(img, idx) in maquina.ultimo_correctivo.imagenes"
                              :key="img.id"
                              :class="['carousel-item', { active: idx === 0 }]"
                            >
                              <img
                                :src="img.imagen"
                                class="d-block w-100 object-fit-cover"
                                style="height: 240px"
                                alt="Evidencia correctivo"
                              />
                            </div>
                          </div>
                          <template v-if="maquina.ultimo_correctivo.imagenes.length > 1">
                            <button
                              class="carousel-control-prev"
                              type="button"
                              data-bs-target="#carouselCorrectivo"
                              data-bs-slide="prev"
                            >
                              <span class="carousel-control-prev-icon"></span>
                            </button>
                            <button
                              class="carousel-control-next"
                              type="button"
                              data-bs-target="#carouselCorrectivo"
                              data-bs-slide="next"
                            >
                              <span class="carousel-control-next-icon"></span>
                            </button>
                          </template>
                        </div>
                      </div>
                    </template>
                  </div>
                </Transition>
              </div>
            </div>
            <!-- /card-body -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { getMaquinaPublica } from "@/utils/api_backend";

const route = useRoute();
const loading = ref(true);
const maquina = ref(null);

const mostrarPreventivo = ref(false);
const mostrarCorrectivo = ref(false);

async function fetchMaquina() {
  try {
    loading.value = true;
    maquina.value = await getMaquinaPublica(route.params.id);
  } catch (err) {
    console.error("Error cargando máquina:", err);
    maquina.value = null;
  } finally {
    loading.value = false;
  }
}

// ── Formateo de fechas ────────────────────────────────────────────
function formatFechaCorta(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
function formatFechaSimple(val) {
  if (!val) return "—";
  // fecha viene como "YYYY-MM-DD" (DateField), evitar desfase de zona
  const [y, m, d] = val.split("-");
  return new Date(+y, +m - 1, +d).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ── Config de criticidad ──────────────────────────────────────────
const CRITICIDAD_MAP = {
  Alta: { cls: "pill-alta", icon: "🔴", desc: "Máquina crítica — operación prioritaria" },
  Media: { cls: "pill-media", icon: "🟡", desc: "Criticidad media — monitoreo regular" },
  Baja: {
    cls: "pill-baja",
    icon: "🟢",
    desc: "Baja criticidad — mantenimiento estándar",
  },
};

const clsCriticidad = computed(
  () => CRITICIDAD_MAP[maquina.value?.criticidad]?.cls ?? "pill-media"
);
const iconCriticidad = computed(
  () => CRITICIDAD_MAP[maquina.value?.criticidad]?.icon ?? "⚙️"
);
const descCriticidad = computed(
  () => CRITICIDAD_MAP[maquina.value?.criticidad]?.desc ?? ""
);

onMounted(fetchMaquina);
</script>

<style scoped>
/* ══ FONDO ═══════════════════════════════════════════════════ */
.public-view {
  font-family: "Inter", sans-serif;
  background: linear-gradient(rgba(20, 30, 40, 0.5), rgba(20, 30, 40, 0.4)),
    url("@/assets/img/Divisan/img_3.jpg");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
}

/* ══ HEADER ══════════════════════════════════════════════════ */
.header-maquina {
  background-color: var(--d-cream);
  border-bottom: 2px solid var(--d-green);
}
.brand-logo-img {
  height: 30px;
}
@media (min-width: 768px) {
  .brand-logo-img {
    height: 36px;
  }
}

/* ══ CRITICIDAD INLINE (sin background) ═════════════════════ */
.criticidad-wrapper {
  padding: 8px 16px;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid rgba(96, 108, 56, 0.15);
  background: #fff;
}
.criticidad-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.82rem;
}
.criticidad-dot-inline {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}
/* dot colors */
.pill-alta {
  background: #b71c1c;
}
.pill-media {
  background: #b98d46;
}
.pill-baja {
  background: #1b5e20;
}
/* text colors */
.pill-alta-txt {
  color: #b71c1c;
  font-weight: 900;
}
.pill-media-txt {
  color: #b98d46;
  font-weight: 900;
}
.pill-baja-txt {
  color: #1b5e20;
  font-weight: 900;
}

.criticidad-tag-inline {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--d-green);
}
.criticidad-valor-inline {
  font-size: 0.9rem;
  font-weight: 900;
}
.criticidad-sep {
  color: rgba(64, 34, 8, 0.3);
  font-size: 0.85rem;
}
.criticidad-desc-inline {
  font-size: 0.78rem;
  color: rgba(64, 34, 8, 0.6);
  font-weight: 500;
}

/* ══ CARD BODY ═══════════════════════════════════════════════ */
.card-body {
  background: #ffffff;
}

/* ── Info básica: 1 columna, 4 renglones ── */
.info-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #ffffff;
  border: 1px solid rgba(96, 108, 56, 0.18);
  border-radius: 14px;
  overflow: hidden;
}
.info-item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  min-width: 0;
}
.info-item:last-child {
  border-bottom: none;
}
.info-item .value-mini {
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 65%;
}
/* Nombre de la máquina: más grande y sin ellipsis para que se vea completo */
.info-item-nombre .value-nombre {
  font-size: 1.05rem;
  font-weight: 900;
  color: var(--d-brown);
  white-space: normal;
  text-align: right;
  max-width: 70%;
}

/* ── Labels y values compartidos ── */
.label-mini {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.68rem;
  color: var(--d-green-dark);
  letter-spacing: 0.04em;
}
.value-mini {
  font-size: 0.9rem;
  color: var(--d-brown);
}

.label-custom {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--d-brown);
  display: block;
  margin-bottom: 6px;
}

/* ── Bloque nombre de rutina ── */
.rutina-bloque {
  background: rgba(96, 108, 56, 0.08);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.rutina-nombre {
  font-size: 0.92rem;
  font-weight: 800;
  color: var(--d-green-dark);
  line-height: 1.4;
  word-break: break-word;
}

/* ── Mini tarjeta de evento ── */
.evento-card {
  background: #f9f9f3;
  border: 1px solid rgba(96, 108, 56, 0.18);
  border-radius: 14px;
  padding: 14px;
  display: grid;
  gap: 6px;
}
.evento-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.88rem;
}
.evento-item .value-mini {
  text-align: right;
}

.badge-tipo {
  background: var(--d-green);
  color: var(--d-cream);
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 800;
  font-size: 0.72rem;
}

.descripcion-box {
  background: rgba(96, 108, 56, 0.08);
  border-left: 4px solid var(--d-green);
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.88rem;
  color: var(--d-brown);
  line-height: 1.5;
}
.falla-box {
  border-left-color: #b71c1c;
  background: rgba(183, 28, 28, 0.05);
}

/* ══ BOTONES COLAPSABLES ════════════════════════════════════ */
.mtto-seccion {
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 14px;
  overflow: hidden;
}

.mtto-toggle {
  width: 100%;
  background: var(--d-cream);
  border: none;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background 0.18s ease;
  text-align: left;
}
.mtto-toggle:hover {
  background: #f5f0c8;
}
.mtto-toggle.abierto {
  background: #f5f0c8;
}

.mtto-toggle-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mtto-toggle-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.preventivo-dot {
  background: #2e7d32;
}
.correctivo-dot {
  background: #b71c1c;
}

.mtto-toggle-label {
  display: block;
  font-weight: 800;
  font-size: 0.82rem;
  color: var(--d-brown);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.mtto-toggle-fecha {
  display: block;
  font-size: 0.75rem;
  color: var(--d-green);
  font-weight: 600;
  margin-top: 1px;
}
.mtto-toggle-ninguno {
  display: block;
  font-size: 0.72rem;
  color: rgba(64, 34, 8, 0.4);
  font-style: italic;
  margin-top: 1px;
}
.mtto-chevron {
  font-size: 0.85rem;
  color: var(--d-green);
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.mtto-detalle {
  padding: 16px;
  border-top: 1px solid rgba(96, 108, 56, 0.12);
  background: #fff;
}

.mtto-vacio {
  font-size: 0.85rem;
  color: rgba(64, 34, 8, 0.4);
  font-style: italic;
  text-align: center;
  padding: 8px 0;
}

/* ══ TRANSICIÓN SLIDE ════════════════════════════════════════ */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
.slide-enter-to,
.slide-leave-from {
  max-height: 1000px;
  opacity: 1;
}

/* ══ UTILES ══════════════════════════════════════════════════ */
.shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.45);
}
.fw-black {
  font-weight: 900;
}
.text-cafe {
  color: var(--d-brown);
}
.text-verde-oscuro {
  color: var(--d-green-dark);
}

.glass-card {
  background: rgba(254, 250, 224, 0.92);
  border-radius: 20px;
  border: 1.5px solid rgba(96, 108, 56, 0.3);
  backdrop-filter: blur(8px);
}
</style>
